import { useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Maximize2, RefreshCw, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
 ComposedChart,
 Area,
 XAxis,
 YAxis,
 CartesianGrid,
 Tooltip,
 ReferenceLine,
 ResponsiveContainer,
} from "recharts";
import type {
 CashflowEvent,
 CashflowEventType,
 CompanyBreakdownItem,
 SalaryCostBreakdownItem,
} from "@/features/enterprises/types";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import { DatePicker } from "@/components/ui/DatePicker";

interface Props {
 events: CashflowEvent[];
 initialBalance?: number;
 onPlannedRevenueClick?: (entityId: string) => void;
 defaultPreset?: ZoomPreset;
 /** If provided, preset + custom date range are persisted in localStorage under this key. */
 storageKey?: string;
 /** Called when the user clicks the refresh button to force data reload. */
 onRefetch?: () => void;
 /** When true: date range snaps to full month boundaries; custom picker shows YYYY-MM inputs. */
 monthMode?: boolean;
 /** monthMode only: default visible range ("YYYY-MM") when nothing is persisted yet.
  * FIN-ARON uses a forward window (current month + N); GK keeps its backward default. */
 initialMonthFrom?: string;
 initialMonthTo?: string;
}

const EVENT_COLORS: Record<string, string> = {
 client_invoice: "#16a34a",
 client_invoice_paid: "#15803d",
 approved_draft: "#4ade80",
 planned_revenue: "#86efac",
 intercompany_revenue: "#0d9488",
 contractor_invoice: "#dc2626",
 contractor_invoice_payment: "#991b1b",
 planned_contractor_cost: "#fca5a5",
 pock_approved_cost: "#f43f5e",
 one_time_cost: "#ea580c",
 recurring_cost: "#f97316",
 intercompany_cost: "#b45309",
 salary_cost: "#7c3aed",
 official_fee_cost: "#111827",
 planned_official_fee_cost: "#6b7280",
 tax_entry: "#b91c1c",
 onetime_aggregate: "#ea580c",
 recurring_aggregate: "#f97316",
 salary_aggregate: "#7c3aed",
};

const EVENT_LABELS: Record<string, string> = {
 client_invoice: "Faktura PRZ — pozostało do wpływu",
 client_invoice_paid: "Faktura PRZ — wpływ",
 approved_draft: "Zatwierdzony szkic PRZ",
 planned_revenue: "Planowany przychód",
 intercompany_revenue: "Refaktury wewnętrzne — przychody",
 contractor_invoice: "Faktura P-GK — pozostało do zapłaty",
 contractor_invoice_payment: "Faktura P-GK — zapłata",
 planned_contractor_cost: "Planowany koszt zamówienia",
 pock_approved_cost: "POCK zatwierdzone — planowany wydatek",
 one_time_cost: "Koszt jednorazowy",
 recurring_cost: "Koszt stały",
 intercompany_cost: "Refaktury wewnętrzne — koszty",
 salary_cost: "Koszty płac",
 official_fee_cost: "Opłata urzędowa (wykonana)",
 planned_official_fee_cost: "Opłata urzędowa (planowana)",
 tax_entry: "Podatek CIT",
 onetime_aggregate: "Koszty jednorazowe i podatki (łącznie)",
 recurring_aggregate: "Koszty stałe (łącznie)",
 salary_aggregate: "Koszty płac (łącznie)",
};

// Order for accordion display
const EVENT_TYPE_ORDER: CashflowEventType[] = [
 "client_invoice_paid",
 "client_invoice",
 "approved_draft",
 "planned_revenue",
 "intercompany_revenue",
 "intercompany_cost",
 "contractor_invoice_payment",
 "contractor_invoice",
 "pock_approved_cost",
 "planned_contractor_cost",
 "one_time_cost",
 "recurring_cost",
 "salary_cost",
 "official_fee_cost",
 "planned_official_fee_cost",
 "tax_entry",
 "onetime_aggregate",
 "recurring_aggregate",
 "salary_aggregate",
];

function tsToLabel(ts: number): string {
 return new Date(ts).toLocaleDateString("pl", {
 day: "2-digit",
 month: "short",
 year: "2-digit",
 });
}

interface DotPoint {
 date: string;
 ts: number;
 balance: number;
 event?: CashflowEvent;
 isCurrentBalance?: boolean;
}

function EventDot(props: {
 cx?: number;
 cy?: number;
 payload?: DotPoint;
 onDotClick?: () => void;
}) {
 const { cx, cy, payload, onDotClick } = props;
 if (!payload?.event || cx === undefined || cy === undefined) return null;
 const ev = payload.event;
 const color = EVENT_COLORS[ev.type] ?? "#6b7280";
 const r = ev.is_actual ? 6 : 5;
 return (
 <g onClick={onDotClick} style={{ cursor: ev.url ? "pointer" : "default" }}>
 <circle cx={cx} cy={cy} r={r + 3} fill={color} fillOpacity={0.15} />
 <circle
 cx={cx}
 cy={cy}
 r={r}
 fill={ev.is_actual ? color : "white"}
 stroke={color}
 strokeWidth={2}
 />
 </g>
 );
}

function CurrentBalanceDot({ cx, cy }: { cx?: number; cy?: number }) {
 if (cx === undefined || cy === undefined) return null;
 return (
 <g>
 <circle cx={cx} cy={cy} r={11} fill="#2563eb" fillOpacity={0.12} />
 <circle
 cx={cx}
 cy={cy}
 r={7}
 fill="#2563eb"
 stroke="#fff"
 strokeWidth={2.5}
 />
 </g>
 );
}

function CustomTooltip({
 active,
 payload,
}: {
 active?: boolean;
 payload?: Array<{ payload?: DotPoint }>;
}) {
 if (!active || !payload?.length) return null;
 const data = payload[0]?.payload;
 if (!data) return null;
 const ev = data.event;

 if (data.isCurrentBalance) {
 return (
 <div
        className="bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-xl shadow-xl p-3 min-w-[220px] text-sm"
 style={{ pointerEvents: "none" }}
 >
 <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">
 {formatDate(data.date)}
 </p>
 <p className="font-semibold text-[var(--color-primary)] dark:text-blue-400 mb-1">
 Obecne saldo
 </p>
 <p
 className={`font-bold text-base ${data.balance >= 0 ? "text-green-600" : "text-red-600"}`}
 >
 {data.balance >= 0 ? "+" : ""}
 {formatAmount(String(data.balance.toFixed(2)))} PLN
 </p>
 </div>
 );
 }

 return (
 <div
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 min-w-[220px] text-sm"
 style={{ pointerEvents: "none" }}
 >
 {ev ? (
 <>
 <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">
 {formatDate(ev.date)}
 </p>
 <p className="font-semibold text-gray-900 dark:text-gray-100 mb-0.5">
 {ev.label}
 </p>
 <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
 {EVENT_LABELS[ev.type]}
 </p>
 <p
 className={`font-bold text-base ${parseFloat(ev.amount) >= 0 ? "text-green-600" : "text-red-600"}`}
 >
 {parseFloat(ev.amount) >= 0 ? "+" : ""}
 {formatAmount(ev.amount)} PLN
 </p>
 <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
 Saldo po zdarzeniu:{" "}
 <strong>{formatAmount(data.balance)} PLN</strong>
 </p>
 <p className="text-xs text-gray-400 dark:text-gray-500">
 {ev.is_actual ? "✓ Zaksięgowane" : "○ Planowane"}
 </p>
 </>
 ) : (
 <>
 <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">
 {formatDate(data.date)}
 </p>
 <p className="font-semibold text-gray-900 dark:text-gray-100">
 Saldo:{" "}
 <span
 className={data.balance >= 0 ? "text-green-600" : "text-red-600"}
 >
 {data.balance >= 0 ? "+" : ""}
 {formatAmount(data.balance)} PLN
 </span>
 </p>
 </>
 )}
 </div>
 );
}

const DAY_MS = 86_400_000;
const MONTH_MS = DAY_MS * 30;

const MONTH_NAMES_PL = [
 "styczeń",
 "luty",
 "marzec",
 "kwiecień",
 "maj",
 "czerwiec",
 "lipiec",
 "sierpień",
 "wrzesień",
 "październik",
 "listopad",
 "grudzień",
];

type ZoomPreset = "2w" | "1m" | "3m" | "6m" | "1y" | "all" | "custom";

// Aggregate recurring_cost and salary_cost events by month for chart display.
// Individual items are preserved in the original events array for the accordion.
function aggregateMonthlyEvents(events: CashflowEvent[]): CashflowEvent[] {
 const result: CashflowEvent[] = [];
 const buckets: Record<string, CashflowEvent[]> = {};

 const MONTHLY_TYPES = new Set([
 "recurring_cost",
 "salary_cost",
 "intercompany_cost",
 "intercompany_revenue",
 ]);

 for (const ev of events) {
 if (MONTHLY_TYPES.has(ev.type)) {
 // salary_cost: group by period label so bucket represents the salary period
 // all others: group by type + payment month
 const bucketKey =
 ev.type === "salary_cost"
 ? `salary_cost::${ev.label}`
 : `${ev.type}::${ev.date.substring(0, 7)}`;
 if (!buckets[bucketKey]) buckets[bucketKey] = [];
 buckets[bucketKey].push(ev);
 } else {
 result.push(ev);
 }
 }

 for (const [key, evs] of Object.entries(buckets)) {
 const type = key.split("::")[0] as CashflowEventType;
 const month = `${evs[0].date.substring(5, 7)}.${evs[0].date.substring(0, 4)}`;
 const totalAmount = evs.reduce((sum, e) => sum + parseFloat(e.amount), 0);
 const label =
 type === "salary_cost"
 ? evs[0].label
 : type === "intercompany_cost"
 ? `Refaktury GK — koszty ${month}`
 : type === "intercompany_revenue"
 ? `Refaktury GK — przychody ${month}`
 : `Koszty stałe ${month}`;
 const url =
 type === "salary_cost"
 ? "/finances/salary"
 : type === "intercompany_cost" || type === "intercompany_revenue"
 ? "/group-finance?tab=refaktury"
 : "/finances/costs";
 result.push({
 date: evs[0].date,
 amount: totalAmount.toFixed(2),
 type,
 label,
 url,
 is_actual: evs.every((e) => e.is_actual),
 });
 }

 return result;
}

interface AccordionGroup {
 type: string;
 events: CashflowEvent[];
 total: number;
}

// Snap a timestamp to the first millisecond of its calendar month.
function snapMonthStart(ts: number): number {
 const d = new Date(ts);
 return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
}
// Snap a timestamp to the last millisecond of its calendar month.
function snapMonthEnd(ts: number): number {
 const d = new Date(ts);
 return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
}
// Return start of month that is `offset` calendar months before/after the month containing `ts`.
// e.g. calMonthOffset(now, -2) = start of month 2 months ago.
function calMonthOffset(ts: number, offset: number): Date {
 const d = new Date(ts);
 return new Date(d.getFullYear(), d.getMonth() + offset, 1);
}

export function CashflowChart({
 events,
 initialBalance,
 onPlannedRevenueClick,
 defaultPreset = "all",
 storageKey,
 onRefetch,
 monthMode = false,
 initialMonthFrom,
 initialMonthTo,
}: Props) {
 const navigate = useNavigate();
 const navigateRef = useRef(navigate);
 useEffect(() => {
 navigateRef.current = navigate;
 }, [navigate]);

 const today = new Date().toISOString().split("T")[0];
 const todayTs = new Date(today).getTime();

 // In monthMode, from/to must be "YYYY-MM" — strip any day part from stored values
 const toMonthStr = (s: string) => (s.length >= 7 ? s.slice(0, 7) : "");
 const defaultMonthFrom = initialMonthFrom ?? (() => {
 const d = new Date(today); d.setMonth(d.getMonth() - 2);
 return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
 })();
 const defaultMonthTo = initialMonthTo ?? (() => {
 const d = new Date(today);
 return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
 })();

 // Restore persisted preset + custom range if storageKey provided
 const persistedPreset = storageKey
 ? (localStorage.getItem(`${storageKey}_preset`) as ZoomPreset | null)
 : null;
 const rawFrom = storageKey ? (localStorage.getItem(`${storageKey}_custom_from`) ?? "") : "";
 const rawTo = storageKey ? (localStorage.getItem(`${storageKey}_custom_to`) ?? "") : "";
 const persistedFrom = monthMode ? (toMonthStr(rawFrom) || defaultMonthFrom) : rawFrom;
 const persistedTo = monthMode ? (toMonthStr(rawTo) || defaultMonthTo) : rawTo;

 // In monthMode the only valid preset is "custom"
 const effectiveDefaultPreset: ZoomPreset = monthMode ? "custom" : (persistedPreset ?? defaultPreset);

 const resolveInitialDomain = (
 preset: ZoomPreset,
 from: string,
 to: string,
 ): [number, number] | null => {
 if (preset === "all") return null;
 const now = new Date(today).getTime();
 const DAY = 86_400_000;
 const MO = DAY * 30;
 // In monthMode "2w" doesn't exist — treat as "1m"
 const effectivePreset = monthMode && preset === "2w" ? "1m" : preset;
 if (effectivePreset === "custom") {
 if (from && to) {
 if (monthMode) {
 // from/to stored as "YYYY-MM"
 const [fy, fm] = from.split("-").map(Number);
 const [ty, tm] = to.split("-").map(Number);
 if (fy && fm && ty && tm) {
 const f = new Date(fy, fm - 1, 1).getTime();
 const t = new Date(ty, tm, 0, 23, 59, 59, 999).getTime();
 if (f < t) return [f, t];
 }
 } else {
 const f = new Date(from).getTime(), t = new Date(to).getTime();
 if (f < t) return [f, t];
 }
 }
 return null;
 }
 if (monthMode) {
 // Strict calendar months: start of month (N-1) months ago → end of current month
 const monthCount = effectivePreset === "1m" ? 1 : effectivePreset === "3m" ? 3 : effectivePreset === "6m" ? 6 : 12;
 const fromDate = calMonthOffset(now, -(monthCount - 1));
 const toDate = calMonthOffset(now, 1); // first of next month
 return [fromDate.getTime(), toDate.getTime() - 1];
 }
 if (effectivePreset === "2w") return [now - DAY * 7, now + DAY * 14];
 if (effectivePreset === "1m") return [now - DAY * 7, now + MO];
 const months = effectivePreset === "3m" ? 3 : effectivePreset === "6m" ? 6 : 12;
 return [now - MO, now + MO * months];
 };

 const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(() =>
 resolveInitialDomain(effectiveDefaultPreset, persistedFrom, persistedTo),
 );
 const [activePreset, setActivePreset] = useState<ZoomPreset>(
 effectiveDefaultPreset,
 );
 const [customFrom, setCustomFrom] = useState(persistedFrom);
 const [customTo, setCustomTo] = useState(persistedTo);
 const [smoothLine, setSmoothLine] = useState(() => {
   const stored = storageKey ? localStorage.getItem(`${storageKey}_smooth`) : null;
   return stored === null ? true : stored !== "0";
 });

 const [legendOpen, setLegendOpen] = useState(false);
 const [isFullscreen, setIsFullscreen] = useState(false);
 const [isPortrait, setIsPortrait] = useState(
  () => typeof window !== "undefined" && window.innerWidth < window.innerHeight
 );
 const [clickedEvent, setClickedEvent] = useState<CashflowEvent | null>(null);

 useEffect(() => {
  const update = () => setIsPortrait(window.innerWidth < window.innerHeight);
  window.addEventListener("resize", update);
  return () => window.removeEventListener("resize", update);
 }, []);

 // Accordion state
 const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
 // Month sub-accordion: keys are "type::YYYY-MM"
 const [openMonthKeys, setOpenMonthKeys] = useState<Set<string>>(new Set());
 // Event breakdown accordion: keys are "type::YYYY-MM-DD::label"
 const [openBreakdownKeys, setOpenBreakdownKeys] = useState<Set<string>>(
 new Set(),
 );

 const toggleMonth = (key: string) => {
 setOpenMonthKeys((prev) => {
 const next = new Set(prev);
 if (next.has(key)) next.delete(key);
 else next.add(key);
 return next;
 });
 };

 const toggleBreakdown = (key: string) => {
 setOpenBreakdownKeys((prev) => {
 const next = new Set(prev);
 if (next.has(key)) next.delete(key);
 else next.add(key);
 return next;
 });
 };

 // Events aggregated by month for chart (recurring + salary → one dot per month)
 const chartEvents = useMemo(() => aggregateMonthlyEvents(events), [events]);

 const chartData = useMemo((): DotPoint[] => {
 const sorted = [...chartEvents].sort((a, b) =>
 a.date.localeCompare(b.date),
 );

 // If initialBalance is provided (group finance), anchor today's value to it.
 // Otherwise (project chart) always start from 0.
 const startingBalance =
 initialBalance !== undefined
 ? Math.round(
 (initialBalance -
 sorted
 .filter((ev) => ev.date < today)
 .reduce((s, ev) => s + parseFloat(ev.amount), 0)) *
 100,
 ) / 100
 : 0;

 if (sorted.length === 0) {
 return [
 { date: today, ts: todayTs - DAY_MS * 30, balance: startingBalance },
 {
 date: today,
 ts: todayTs,
 balance: startingBalance,
 isCurrentBalance: true,
 },
 ];
 }

 const firstTs = new Date(sorted[0].date).getTime();
 const lastTs = new Date(sorted[sorted.length - 1].date).getTime();
 const span = Math.max(lastTs - firstTs, DAY_MS);
 const pad = Math.max(span * 0.04, DAY_MS * 2);

 const points: DotPoint[] = [
 { date: sorted[0].date, ts: firstTs - pad, balance: startingBalance },
 ];

 let running = startingBalance;
 const usedTs = new Set<number>();
 for (const ev of sorted) {
 running = Math.round((running + parseFloat(ev.amount)) * 100) / 100;
 // Place today's events 1 hour before the "dziś" reference line so they appear to its left
 let ts =
 ev.date === today ? todayTs - 3_600_000 : new Date(ev.date).getTime();
 while (usedTs.has(ts)) ts += 1;
 usedTs.add(ts);
 points.push({ date: ev.date, ts, balance: running, event: ev });
 }

 // Find balance at today (after all events on/before today)
 let currentBal = startingBalance;
 for (const p of points) {
 if (p.ts <= todayTs) currentBal = p.balance;
 else break;
 }

 // Insert synthetic "current balance" point at todayTs
 let cbTs = todayTs;
 while (usedTs.has(cbTs)) cbTs += 1;
 usedTs.add(cbTs);
 let insertIdx = points.findIndex((p) => p.ts > cbTs);
 if (insertIdx === -1) insertIdx = points.length;
 points.splice(insertIdx, 0, {
 date: today,
 ts: cbTs,
 balance: currentBal,
 isCurrentBalance: true,
 });

 points.push({
 date: sorted[sorted.length - 1].date,
 ts: lastTs + pad,
 balance: running,
 });
 return points;
 }, [chartEvents, today, todayTs, initialBalance]);

 const dataMinTs = chartData.length > 0 ? chartData[0].ts : todayTs - MONTH_MS;
 const dataMaxTs =
 chartData.length > 0
 ? chartData[chartData.length - 1].ts
 : todayTs + MONTH_MS;

 // Filter chartData to the zoom window so Recharts actually clips the rendered area.
 // Recharts' domain prop only changes axis labels — it does not hide out-of-range points.
 const visibleData = useMemo(() => {
 if (!zoomDomain) return chartData;
 const [start, end] = zoomDomain;
 // Find the running balance just before the window
 let preBalance =
 chartData.length > 0 ? chartData[0].balance : (initialBalance ?? 0);
 for (const p of chartData) {
 if (p.ts <= start) preBalance = p.balance;
 else break;
 }
 const inRange = chartData.filter((p) => p.ts > start && p.ts <= end);
 const lastBalance =
 inRange.length > 0 ? inRange[inRange.length - 1].balance : preBalance;
 return [
 { date: "", ts: start, balance: preBalance } as (typeof chartData)[0],
 ...inRange,
 { date: "", ts: end, balance: lastBalance } as (typeof chartData)[0],
 ];
 }, [chartData, zoomDomain]);

 const domain: [number, number] = zoomDomain ?? [dataMinTs, dataMaxTs];

 const applyPreset = (preset: ZoomPreset) => {
 setActivePreset(preset);
 if (storageKey) localStorage.setItem(`${storageKey}_preset`, preset);
 if (preset === "all") {
 setZoomDomain(null);
 } else if (preset === "custom") {
 // domain applied separately via applyCustomRange
 } else if (monthMode) {
 // Strict calendar months: from start of month N months ago → end of current month
 const monthCount = preset === "1m" ? 1 : preset === "3m" ? 3 : preset === "6m" ? 6 : 12;
 const from = calMonthOffset(todayTs, -(monthCount - 1));
 const to = calMonthOffset(todayTs, 1); // first day of next month = end of current
 setZoomDomain([from.getTime(), to.getTime() - 1]);
 } else if (preset === "2w") {
 setZoomDomain([todayTs - DAY_MS * 7, todayTs + DAY_MS * 14]);
 } else if (preset === "1m") {
 setZoomDomain([todayTs - DAY_MS * 7, todayTs + MONTH_MS]);
 } else {
 const months = preset === "3m" ? 3 : preset === "6m" ? 6 : 12;
 setZoomDomain([todayTs - MONTH_MS, todayTs + MONTH_MS * months]);
 }
 };

 const applyCustomRange = (from: string, to: string) => {
 if (!from || !to) return;
 let fromTs: number;
 let toTs: number;
 if (monthMode) {
 // from/to are "YYYY-MM" — snap to full month boundaries
 const [fy, fm] = from.split("-").map(Number);
 const [ty, tm] = to.split("-").map(Number);
 fromTs = new Date(fy, fm - 1, 1).getTime();
 toTs = new Date(ty, tm, 0, 23, 59, 59, 999).getTime();
 } else {
 fromTs = new Date(from).getTime();
 toTs = new Date(to).getTime();
 }
 if (fromTs >= toTs) return;
 setZoomDomain([fromTs, toTs]);
 if (storageKey) {
 localStorage.setItem(`${storageKey}_custom_from`, from);
 localStorage.setItem(`${storageKey}_custom_to`, to);
 }
 };

 // Accordion groups from original (non-aggregated) events, filtered to current zoom window
 const accordionGroups = useMemo<AccordionGroup[]>(() => {
 const filteredEvents = zoomDomain
 ? events.filter((ev) => {
 const ts = new Date(ev.date).getTime();
 return ts >= zoomDomain[0] && ts <= zoomDomain[1];
 })
 : events;
 const byType: Record<string, CashflowEvent[]> = {};
 for (const ev of filteredEvents) {
 if (!byType[ev.type]) byType[ev.type] = [];
 byType[ev.type].push(ev);
 }
 return EVENT_TYPE_ORDER.filter((t) => byType[t]).map((type) => ({
 type,
 events: [...byType[type]].sort((a, b) => a.date.localeCompare(b.date)),
 total: byType[type].reduce((s, e) => s + parseFloat(e.amount), 0),
 }));
 }, [events, zoomDomain]);

 // Legenda pokazuje tylko typy faktycznie obecne na wykresie (jak lista „Szczegóły zdarzeń"),
 // nie wszystkie możliwe — inaczej legenda jest nieczytelnym spisem ~20 nieużywanych pozycji.
 const presentTypes = useMemo(
 () => new Set(accordionGroups.map((g) => g.type)),
 [accordionGroups],
 );

 const allGroupTypes = accordionGroups.map((g) => g.type);
 const allOpen =
 allGroupTypes.length > 0 && allGroupTypes.every((t) => openGroups.has(t));

 const toggleAll = () => {
 if (allOpen) {
 setOpenGroups(new Set());
 } else {
 setOpenGroups(new Set(allGroupTypes));
 }
 };

 const toggleGroup = (type: string) => {
 setOpenGroups((prev) => {
 const next = new Set(prev);
 if (next.has(type)) next.delete(type);
 else next.add(type);
 return next;
 });
 };

 const lineColor = "#2563eb";

 const formatY = (value: number) => {
 const abs = Math.abs(value);
 if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
 if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
 return value.toString();
 };

 const btnBase = "px-2.5 py-1 text-xs rounded font-medium transition-colors";
 const btnActive = "bg-[var(--color-primary)] text-white";
 const btnInactive =
 "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200";

 const fsStyle: React.CSSProperties = isPortrait
  ? {
      position: "fixed",
      top: "50%",
      left: "50%",
      width: "100vh",
      height: "100vw",
      transform: "translate(-50%, -50%) rotate(90deg)",
      overflow: "auto",
    }
  : { position: "fixed", inset: 0, overflow: "auto" };

 const inner = (
 <div>
 {isFullscreen && (
  <div className="flex items-center justify-between px-4 py-2 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
   <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Cashflow</span>
   <button
    onClick={() => setIsFullscreen(false)}
    className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded"
   >
    <X size={18} />
   </button>
  </div>
 )}
 {isFullscreen && clickedEvent && (
  <div
   className="fixed inset-0 z-10 flex items-center justify-center bg-black/40"
   onClick={() => setClickedEvent(null)}
  >
   <div
    className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-4 w-64 max-w-[85%]"
    onClick={(e) => e.stopPropagation()}
   >
    <div className="flex items-start justify-between mb-2">
     <div className="min-w-0">
      <p className="text-xs text-gray-400 dark:text-gray-500">{formatDate(clickedEvent.date)}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{EVENT_LABELS[clickedEvent.type] ?? clickedEvent.type}</p>
     </div>
     <button onClick={() => setClickedEvent(null)} className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
      <X size={16} />
     </button>
    </div>
    <p className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2 leading-snug">{clickedEvent.label}</p>
    <p className={`font-bold text-xl mb-1 ${parseFloat(clickedEvent.amount) >= 0 ? "text-green-600" : "text-red-600"}`}>
     {parseFloat(clickedEvent.amount) >= 0 ? "+" : ""}
     {formatAmount(clickedEvent.amount)} PLN
    </p>
    <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
     {clickedEvent.is_actual ? "✓ Zaksięgowane" : "○ Planowane"}
    </p>
    {clickedEvent.url && (
     <button
      onClick={() => {
       setClickedEvent(null);
       setIsFullscreen(false);
       navigateRef.current(clickedEvent.url!);
      }}
      className="w-full py-2 text-sm font-medium text-white bg-[var(--color-primary)] rounded-lg hover:opacity-90 transition-opacity"
     >
      Przejdź →
     </button>
    )}
   </div>
  </div>
 )}
 {/* Chart */}
 <div className="bg-white dark:bg-gray-800 p-4">
 {/* Range controls */}
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
 <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 sm:pb-0 sm:flex-wrap">
 <span className="text-xs text-gray-400 dark:text-gray-500 mr-1">
 Zakres:
 </span>
 {monthMode ? null : (
 (["2w", "1m", "3m", "6m", "1y", "all", "custom"] as ZoomPreset[]).map((p) => (
 <button
 key={p}
 className={`${btnBase} ${activePreset === p ? btnActive : btnInactive}`}
 onClick={() => applyPreset(p)}
 >
 {p === "2w" ? "2 tyg." : p === "1m" ? "1 mies." : p === "3m" ? "3 mies."
 : p === "6m" ? "6 mies." : p === "1y" ? "1 rok" : p === "all" ? "Wszystko" : "inne"}
 </button>
 ))
 )}
 {monthMode ? (
 <>
 <span className="text-xs font-medium text-[var(--color-primary)] dark:text-blue-400 mr-1">Miesiące księgowe:</span>
              <input
 type="month"
 className="border border-gray-300 dark:border-gray-600 rounded px-2 py-0.5 text-xs bg-white dark:bg-gray-700"
 value={customFrom}
 onChange={(e) => { setCustomFrom(e.target.value); applyCustomRange(e.target.value, customTo); }}
 />
 <span className="text-xs text-gray-400">—</span>
              <input
 type="month"
 className="border border-gray-300 dark:border-gray-600 rounded px-2 py-0.5 text-xs bg-white dark:bg-gray-700"
 value={customTo}
 onChange={(e) => { setCustomTo(e.target.value); applyCustomRange(customFrom, e.target.value); }}
 />
 </>
 ) : activePreset === "custom" ? (
 <>
              <DatePicker
 value={customFrom}
 onChange={(v) => { setCustomFrom(v); applyCustomRange(v, customTo); }}
 />
 <span className="text-xs text-gray-400">—</span>
              <DatePicker
 value={customTo}
 onChange={(v) => { setCustomTo(v); applyCustomRange(customFrom, v); }}
 />
 </>
 ) : null}
 </div>
 <div className="flex items-center gap-3">
 <label className="flex items-center gap-2 cursor-pointer select-none">
 <span className="text-xs text-gray-400 dark:text-gray-500">Prosta</span>
 <button
 role="switch"
 aria-checked={smoothLine}
 onClick={() => {
 const next = !smoothLine;
 setSmoothLine(next);
 if (storageKey) localStorage.setItem(`${storageKey}_smooth`, next ? "1" : "0");
 }}
 className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-1 ${smoothLine ? "bg-[var(--color-primary)]" : "bg-gray-200 dark:bg-gray-600"}`}
 >
 <span
 className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${smoothLine ? "translate-x-4" : "translate-x-0"}`}
 />
 </button>
 <span className="text-xs text-gray-400 dark:text-gray-500">Płynna</span>
 </label>
 {onRefetch && (
 <button
 onClick={onRefetch}
 title="Odśwież dane"
 className="flex items-center gap-1 px-2.5 py-1 text-xs rounded font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-[var(--color-primary-dim)] hover:text-[var(--color-primary)] dark:hover:bg-blue-900/30 dark:hover:text-[var(--color-primary)] transition-colors"
 >
 <RefreshCw size={12} />
 Odśwież
 </button>
 )}
 {!isFullscreen && (
 <button
 onClick={() => setIsFullscreen(true)}
 title="Pełny ekran (poziomo)"
 className="sm:hidden flex items-center justify-center w-8 h-8 rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity flex-shrink-0"
 >
 <Maximize2 size={15} />
 </button>
 )}
 </div>
 </div>

 <div className={isFullscreen ? "h-[240px]" : "h-[200px] sm:h-[380px]"}>
 <ResponsiveContainer width="100%" height="100%">
 <ComposedChart
 data={visibleData}
 margin={{ top: 16, right: 16, left: 4, bottom: 5 }}
 >
 <defs>
 <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
 <stop offset="5%" stopColor={lineColor} stopOpacity={0.25} />
 <stop offset="95%" stopColor={lineColor} stopOpacity={0.02} />
 </linearGradient>
 </defs>

 <CartesianGrid
 strokeDasharray="3 3"
 stroke="#f0f0f0"
 vertical={false}
 />

 <XAxis
 type="number"
 dataKey="ts"
 scale="time"
 domain={domain}
 tickFormatter={tsToLabel}
 tick={{ fontSize: 11, fill: "#9ca3af" }}
 axisLine={false}
 tickLine={false}
 tickCount={6}
 />

 <YAxis
 tickFormatter={formatY}
 tick={{ fontSize: 11, fill: "#9ca3af" }}
 axisLine={false}
 tickLine={false}
 width={55}
 />

 {!isFullscreen && (
 <Tooltip
 content={<CustomTooltip />}
 isAnimationActive={false}
 wrapperStyle={{ pointerEvents: "none", zIndex: 50 }}
 />
 )}

 <ReferenceLine y={0} stroke="#d1d5db" strokeWidth={1.5} />

 <ReferenceLine
 x={todayTs}
 stroke="#3b82f6"
 strokeDasharray="5 4"
 strokeWidth={1.5}
 label={{
 value: "dziś",
 position: "top",
 fill: "#3b82f6",
 fontSize: 11,
 }}
 />

 <ReferenceLine
 x={todayTs + DAY_MS * 30}
 stroke="#9ca3af"
 strokeDasharray="4 4"
 strokeWidth={1}
 label={{
 value: "30 dni",
 position: "top",
 fill: "#9ca3af",
 fontSize: 10,
 }}
 />

 <ReferenceLine
 x={todayTs + DAY_MS * 60}
 stroke="#9ca3af"
 strokeDasharray="4 4"
 strokeWidth={1}
 label={{
 value: "60 dni",
 position: "top",
 fill: "#9ca3af",
 fontSize: 10,
 }}
 />

 <Area
 type={smoothLine ? "monotone" : "linear"}
 dataKey="balance"
 stroke={lineColor}
 strokeWidth={2.5}
 fill="url(#areaFill)"
 activeDot={{
 r: 4,
 fill: lineColor,
 stroke: "#fff",
 strokeWidth: 2,
 }}
 isAnimationActive={false}
 dot={(dotProps: unknown) => {
 const p = dotProps as {
 cx?: number;
 cy?: number;
 payload?: DotPoint;
 index?: number;
 };
 if (p.payload?.isCurrentBalance) {
 return (
 <CurrentBalanceDot
 key="current-balance"
 cx={p.cx}
 cy={p.cy}
 />
 );
 }
 if (!p.payload?.event) return <g key={`nd-${p.index}`} />;
 return (
 <EventDot
 key={`ev-${p.payload.ts}`}
 cx={p.cx}
 cy={p.cy}
 payload={p.payload}
 onDotClick={() => {
 if (isFullscreen) {
  if (p.payload?.event) setClickedEvent(p.payload.event);
 } else if (p.payload?.event?.url) {
  navigateRef.current(p.payload.event.url);
 }
 }}
 />
 );
 }}
 />
 </ComposedChart>
 </ResponsiveContainer>
 </div>

 {/* Legend */}
 <div className="mt-3">
 {/* Mobile toggle */}
 <button
 className="sm:hidden text-xs text-gray-400 flex items-center gap-1 mb-1"
 onClick={() => setLegendOpen((v) => !v)}
 >
 Legenda {legendOpen ? "▲" : "▼"}
 </button>
 <div className={`${legendOpen ? "grid" : "hidden"} sm:flex grid-cols-2 sm:flex-wrap gap-x-3 sm:gap-x-4 gap-y-1 sm:gap-y-2 sm:justify-center`}>
 {Object.entries(EVENT_COLORS)
 .filter(([type]) => presentTypes.has(type))
 .map(([type, color]) => (
 <span
 key={type}
 className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400"
 >
 <span
 className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0"
 style={{ background: color }}
 />
 {EVENT_LABELS[type]}
 </span>
 ))}
 <span className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
 <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0 bg-white dark:bg-gray-800 border-2 border-gray-400" />
 Planowane
 </span>
 <span className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
 <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0 bg-[var(--color-primary)] border-2 border-white" />
 Obecne saldo
 </span>
 </div>
 </div>
 </div>

 {/* Accordion event list */}
 {accordionGroups.length > 0 && (
 <div className="mt-4">
 <div className="flex items-center justify-between mb-2">
 <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
 Szczegóły zdarzeń
 </h3>
 <button
 onClick={toggleAll}
 className="text-xs text-[var(--color-primary)] hover:underline font-medium"
 >
 {allOpen ? "Zwiń wszystkich" : "Rozwiń wszystkich"}
 </button>
 </div>
 <div className="flex flex-col gap-2">
 {accordionGroups.map((group) => {
 const isOpen = openGroups.has(group.type);
 const color = EVENT_COLORS[group.type] ?? "#6b7280";
 const isPositiveGroup = group.total >= 0;
 return (
 <div
 key={group.type}
 className="bg-white dark:bg-gray-800 overflow-hidden"
 >
 <button
 onClick={() => toggleGroup(group.type)}
 className="w-full px-3 sm:px-4 py-2 sm:py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
 >
 {/* Desktop: single row */}
 <div className="hidden sm:flex items-center gap-3">
 <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: color }} />
 <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200">
 {EVENT_LABELS[group.type] ?? group.type}
 </span>
 <span className="text-xs text-gray-400 dark:text-gray-500 mr-2 whitespace-nowrap">
 {group.events.length}{" "}{group.events.length === 1 ? "zdarzenie" : "zdarzeń"}
 </span>
 <span className={`text-sm font-semibold whitespace-nowrap ${isPositiveGroup ? "text-green-700" : "text-red-700"}`}>
 {isPositiveGroup ? "+" : ""}{formatAmount(group.total.toFixed(2))} PLN
 </span>
 <span className="text-gray-400 dark:text-gray-500 ml-2 text-xs">{isOpen ? "▲" : "▼"}</span>
 </div>
 {/* Mobile: label line + meta line */}
 <div className="sm:hidden flex items-center gap-2">
 <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
 <span className="flex-1 text-xs font-medium text-gray-800 dark:text-gray-200">
 {EVENT_LABELS[group.type] ?? group.type}
 </span>
 <span className={`text-xs font-bold whitespace-nowrap ml-2 ${isPositiveGroup ? "text-green-700" : "text-red-700"}`}>
 {isPositiveGroup ? "+" : ""}{formatAmount(group.total.toFixed(2))} PLN
 </span>
 <span className="text-gray-400 dark:text-gray-500 text-xs ml-1">{isOpen ? "▲" : "▼"}</span>
 </div>
 </button>

 {isOpen && (
 <div className="border-t border-gray-100 dark:border-gray-700">
 {group.type === "recurring_cost" ||
 group.type === "salary_cost" ||
 group.type === "intercompany_cost" ||
 group.type === "intercompany_revenue" ? (
 (() => {
 // Group by month — two-level accordion
 const byMonth: Record<string, CashflowEvent[]> = {};
 for (const ev of group.events) {
 const m = ev.date.substring(0, 7);
 if (!byMonth[m]) byMonth[m] = [];
 byMonth[m].push(ev);
 }
 return (
 <div className="divide-y divide-gray-100 dark:divide-gray-700">
 {Object.entries(byMonth)
 .sort(([a], [b]) => a.localeCompare(b))
 .map(([month, evs]) => {
 const monthKey = `${group.type}::${month}`;
 const isMonthOpen =
 openMonthKeys.has(monthKey);
 const monthTotal = evs.reduce(
 (s, e) => s + parseFloat(e.amount),
 0,
 );
 const [y, mo] = month.split("-").map(Number);
 const monthLabel = `${MONTH_NAMES_PL[mo - 1]} ${y}`;
 return (
 <div key={month}>
 <button
 onClick={() => toggleMonth(monthKey)}
 className="w-full flex items-center gap-3 px-5 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-sm"
 >
 <span className="flex-1 text-gray-600 dark:text-gray-400">
 {monthLabel}
 </span>
 <span
 className={`text-xs font-semibold ${monthTotal >= 0 ? "text-green-700" : "text-red-700"}`}
 >
 {monthTotal >= 0 ? "+" : ""}
 {formatAmount(
 monthTotal.toFixed(2),
 )}{" "}
 PLN
 </span>
 <span className="text-gray-400 text-xs ml-1">
 {isMonthOpen ? "▲" : "▼"}
 </span>
 </button>
 {isMonthOpen && (
 <div className="border-t border-gray-100 dark:border-gray-700">
 {evs.map((ev, i) => {
 const amt = parseFloat(ev.amount);
 const bKey = `${group.type}::${ev.date}::${ev.label}`;
 const hasBreakdown =
 ev.breakdown &&
 ev.breakdown.length > 0;
 const isBreakdownOpen =
 openBreakdownKeys.has(bKey);
 return (
 <div
 key={i}
 className="border-b border-gray-50 dark:border-gray-700/50 last:border-0"
 >
 <div className="flex items-center hover:bg-gray-50 dark:hover:bg-gray-700/50">
 <span className="px-3 sm:px-8 py-1.5 text-gray-400 dark:text-gray-500 text-xs whitespace-nowrap w-20 sm:w-24 flex-shrink-0">
 {formatDate(ev.date)}
 </span>
 <span className="px-2 sm:px-3 py-1.5 text-gray-700 dark:text-gray-300 text-xs flex-1">
 {ev.label}
 </span>
 <span className="px-2 sm:px-4 py-1.5 text-right whitespace-nowrap">
 <span
 className={`font-semibold text-xs ${amt >= 0 ? "text-green-700" : "text-red-700"}`}
 >
 {amt >= 0 ? "+" : ""}
 {formatAmount(
 ev.amount,
 )}{" "}
 PLN
 </span>
 </span>
 <span className="px-3 py-1.5 text-right text-xs text-gray-400 dark:text-gray-500 w-6">
 {ev.is_actual ? "✓" : "○"}
 </span>
 {hasBreakdown && (
 <button
 onClick={() =>
 toggleBreakdown(bKey)
 }
 className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600 flex-shrink-0"
 title="Rozwiń rozbicie"
 >
 {isBreakdownOpen
 ? "▲"
 : "▼"}
 </button>
 )}
 </div>
 {hasBreakdown &&
 isBreakdownOpen && (
 <table className="w-full text-xs border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
 <thead>
 <tr className="text-gray-400 dark:text-gray-500">
 <th className="px-10 py-1 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
 Pracownik
 </th>
 <th className="px-3 py-1 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
 Alokacja
 </th>
 <th className="px-3 py-1 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
 Koszt mies.
 </th>
 <th className="px-4 py-1 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
 Udział
 </th>
 </tr>
 </thead>
 <tbody>
 {(
 ev.breakdown as SalaryCostBreakdownItem[]
 ).map((b, bi) => (
 <tr
 key={bi}
 className="border-t border-gray-100 dark:border-gray-700/50"
 >
 <td className="px-10 py-1 text-gray-600 dark:text-gray-400">
 {b.employee_name}
 </td>
 <td className="px-3 py-1 text-right text-gray-500">
 {(
 b.fraction * 100
 ).toFixed(1)}
 %
 </td>
 <td className="px-3 py-1 text-right text-gray-500">
 {formatAmount(
 b.monthly_cost,
 )}{" "}
 PLN
 </td>
 <td className="px-4 py-1 text-right font-semibold text-red-700">
 {formatAmount(
 b.cost,
 )}{" "}
 PLN
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 )}
 </div>
 );
 })}
 </div>
 )}
 </div>
 );
 })}
 </div>
 );
 })()
 ) : (
 <div className="divide-y divide-gray-50 dark:divide-gray-700">
 {group.events.map((ev, i) => {
 const amt = parseFloat(ev.amount);
 const isPlannedRevenue = ev.type === "planned_revenue";
 const hasCompanyBreakdown =
 ev.type === "salary_aggregate" &&
 ev.company_breakdown &&
 ev.company_breakdown.length > 0;
 const bKey = `${group.type}::${ev.date}::${ev.label}`;
 const isCompanyOpen = openBreakdownKeys.has(bKey);
 return (
 <div key={i}>
 <div className="flex items-center hover:bg-gray-50 dark:hover:bg-gray-700/50">
 <span className="px-2 sm:px-4 py-1.5 sm:py-2 text-gray-400 dark:text-gray-500 text-xs whitespace-nowrap w-20 sm:w-24 flex-shrink-0">
 {formatDate(ev.date)}
 </span>
 <span className="px-2 sm:px-4 py-1.5 sm:py-2 text-gray-800 dark:text-gray-200 flex-1 text-xs sm:text-sm">
 {isPlannedRevenue &&
 ev.entity_id &&
 onPlannedRevenueClick ? (
 <button
 onClick={() =>
 onPlannedRevenueClick(ev.entity_id!)
 }
 className="text-[var(--color-primary)] hover:underline text-left"
 >
 {ev.label}
 </button>
 ) : ev.url ? (
 <button
 onClick={() =>
 navigateRef.current(ev.url)
 }
 className="text-[var(--color-primary)] hover:underline text-left"
 >
 {ev.label}
 </button>
 ) : (
 ev.label
 )}
 </span>
 <span className="px-2 sm:px-4 py-1.5 sm:py-2 text-right whitespace-nowrap">
 <span
 className={`font-semibold text-xs ${amt >= 0 ? "text-green-700" : "text-red-700"}`}
 >
 {amt >= 0 ? "+" : ""}
 {formatAmount(ev.amount)} PLN
 </span>
 </span>
 <span className="px-2 sm:px-4 py-1.5 sm:py-2 text-right text-xs text-gray-400 dark:text-gray-500 w-6 sm:w-8">
 {ev.is_actual ? "✓" : "○"}
 </span>
 {hasCompanyBreakdown && (
 <button
 onClick={() => toggleBreakdown(bKey)}
 className="px-3 py-2 text-xs text-gray-400 hover:text-gray-600 flex-shrink-0"
 title="Rozwiń per firma"
 >
 {isCompanyOpen ? "▲" : "▼"}
 </button>
 )}
 </div>
 {hasCompanyBreakdown && isCompanyOpen && (
 <table className="w-full text-xs border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
 <thead>
 <tr className="text-gray-400 dark:text-gray-500">
 <th className="px-10 py-1 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Firma</th>
 <th className="px-4 py-1 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kwota</th>
 </tr>
 </thead>
 <tbody>
 {(ev.company_breakdown as CompanyBreakdownItem[]).map((b, bi) => (
 <tr
 key={bi}
 className="border-t border-gray-100 dark:border-gray-700/50"
 >
 <td className="px-10 py-1 text-gray-600 dark:text-gray-400 font-medium">
 {b.company_code}
 </td>
 <td className="px-4 py-1 text-right font-semibold text-red-700">
 {formatAmount(b.amount)} PLN
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 )}
 </div>
 );
 })}
 </div>
 )}
 </div>
 )}
 </div>
 );
 })}
 </div>
 </div>
 )}
 </div>
 );

 if (isFullscreen) {
  return createPortal(
   <div className="fixed z-[100] bg-white dark:bg-gray-900" style={fsStyle}>
    {inner}
   </div>,
   document.body
  );
 }
 return inner;
}
